import React from 'react';
import { useLocation, useNavigate, useSearchParams, matchPath } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { createSupplierQuotation, updateSupplierQuotation } from '../../../services/mutations';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  PlusIcon,
  RefreshIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../../components/ui';

import { moduleConfig } from './supplierQuotationsModuleConfig';
import { SupplierQuotationExpense, SupplierQuotationLine, useSupplierQuotation } from './useData';
import { formatCaseParenthetical } from '../requisitions/caseDisplay';
import { supplyBlockedActionMessage } from '../blockingMessages';

type LineForm = {
  key: string;
  /** Filters Item dropdown to catalog rows with this `StoreItem.level1` (empty = all). */
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  unitId: string;
  qty: string;
  unitPrice: string;
  unitTotal: string;
  lineTotal: string;
  discount: string;
  vat: string;
  itemNotes: string;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  level1Filter: '',
  itemId: '',
  itemVcode: '',
  itemDescr: '',
  unitId: '',
  qty: '',
  unitPrice: '',
  unitTotal: '',
  lineTotal: '',
  discount: '',
  vat: '',
  itemNotes: '',
});

type ExpenseForm = {
  key: string;
  description: string;
  value: string;
  vat: string;
};

const newExpense = (): ExpenseForm => ({
  key: `exp-${Date.now()}-${Math.random()}`,
  description: '',
  value: '',
  vat: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const asQtyInteger = (value: string): number | undefined => {
  const n = asNumber(value);
  return n === undefined ? undefined : Math.round(n);
};

const toFixed2String = (value: number): string => value.toFixed(2);

/** Whole-number qty for input/display (no fractional decimals). */
const qtyToFormString = (qty: number | null | undefined): string =>
  qty != null && Number.isFinite(Number(qty)) ? String(Math.round(Number(qty))) : '';

const normalizeTo2Dp = (value: string): string => {
  if (!value || value.trim() === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? toFixed2String(n) : value;
};

const computeUnitTotal = (qty: string, unitPrice: string): string => {
  const q = Number(qty);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '';
  const total = q * p;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};

const computeFinalTotal = (unitTotal: string, discount: string, vat: string): string => {
  const u = Number(unitTotal);
  const d = Number(discount || '0');
  const v = Number(vat || '0');
  if (!Number.isFinite(u) || !Number.isFinite(d) || !Number.isFinite(v)) return '';
  const total = u - d + v;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};

const computeCurrencyTotal = (lineTotal: string, rate: string): string => {
  const t = Number(lineTotal);
  const r = Number(rate);
  if (!Number.isFinite(t) || !Number.isFinite(r)) return '';
  const total = t * r;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};

const asFinite = (value: string): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (n: number): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function formatQuotationItemLabel(
  vendorCode: string | null | undefined,
  name: string | null | undefined,
  unitId: string | number | null | undefined,
  units: { id: number; ucode?: string }[],
  unitUcodeFromItem?: string | null,
): string {
  const v = (vendorCode ?? '').trim() || '';
  const n = (name ?? '').trim() || '';
  return `${v} - ${n}`;
}

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const [searchParams] = useSearchParams();
  const editMatch = matchPath(
    { path: '/supplies/supplier-quotations/:id/edit', end: true },
    location.pathname,
  );
  const isEdit = editMatch != null;
  const rawId = editMatch?.params.id;
  const quotationId =
    rawId != null && /^\d+$/.test(String(rawId)) ? Number(rawId) : undefined;
  const paramEnquiryId = React.useMemo(() => {
    const e = searchParams.get('enquiryId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);

  const { data: existing, isLoading: loadingExisting } = useSupplierQuotation(quotationId);
  const { data: evaluationsRes } = useSWR<any>(isEdit ? '/quotation-evaluations?page=1&limit=1000' : null, fetcher);
  const { data: purchaseOrdersRes } = useSWR<any>(isEdit ? '/supply-purchase-orders?page=1&limit=1000' : null, fetcher);
  const { data: units = [] } = useSWR<any[]>('/store-units', fetcher);
  const { data: enquiriesRes } = useSWR<any>('/supplier-enquiries?page=1&limit=500', fetcher);
  const { data: currenciesRes } = useSWR<any>('/currencies?page=1&limit=500', fetcher);
  const enquiries = React.useMemo(
    () => (Array.isArray(enquiriesRes) ? enquiriesRes : enquiriesRes?.data ?? []),
    [enquiriesRes],
  );

  const [selectedEnquiryId, setSelectedEnquiryId] = React.useState('');
  const { data: selectedEnquiry } = useSWR<any>(
    selectedEnquiryId ? `/supplier-enquiries/${selectedEnquiryId}` : null,
    fetcher,
  );

  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({
    quoDate: '',
    ref: '',
    notes: '',
    rate: '',
    paymentTerms: '',
    supplierRef: '',
    currencyId: '',
  });
  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);
  const [expenses, setExpenses] = React.useState<ExpenseForm[]>([newExpense()]);
  const [bulkDiscountPct, setBulkDiscountPct] = React.useState('');
  const [bulkVatPct, setBulkVatPct] = React.useState('');

  const { data: itemsRes } = useSWR<any>('/store-items?page=1&limit=200', fetcher);
  const items = React.useMemo(
    () => (Array.isArray(itemsRes) ? itemsRes : itemsRes?.data ?? []),
    [itemsRes],
  );
  const selectedLevel1Values = React.useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .map((line) => line.level1Filter?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [lines],
  );
  const { data: levelItemsMap = {} } = useSWR<Record<string, any[]>>(
    selectedLevel1Values.length > 0 ? ['quotation-line-level-items', ...selectedLevel1Values] : null,
    async () => {
      const entries = await Promise.all(
        selectedLevel1Values.map(async (level1) => {
          const params = new URLSearchParams();
          params.set('filters', JSON.stringify({ filters: { level1: [level1] } }));
          const response = await fetcher(`/store-items?${params.toString()}`);
          const levelItems = Array.isArray(response) ? response : response?.data ?? [];
          return [level1, levelItems] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    for (const levelItems of Object.values(levelItemsMap)) {
      for (const item of levelItems) {
        map.set(String(item.id), item);
      }
    }
    return map;
  }, [items, levelItemsMap]);

  const enquiryOptions = React.useMemo(() => {
    const rows = enquiries.map((e: any) => ({
      value: String(e.id),
      label: `${e.ref || `ENQ-${e.id}`}${e.supplier?.name ? ` · ${e.supplier.name}` : ''}`,
    }));
    if (existing?.enqId && existing?.enquiry && !rows.some((o: any) => o.value === String(existing.enqId))) {
      rows.push({ value: String(existing.enqId), label: existing.enquiry.ref || `ENQ-${existing.enqId}` });
    }
    return [{ value: '', label: 'Select enquiry' }, ...rows];
  }, [enquiries, existing?.enqId, existing?.enquiry]);

  const currencies = React.useMemo(
    () => (Array.isArray(currenciesRes) ? currenciesRes : currenciesRes?.data ?? []) as {
      id: number;
      code: string;
      name: string;
      symbol?: string | null;
      isActive?: boolean;
    }[],
    [currenciesRes],
  );

  const currencyOptions = React.useMemo(() => {
    const active = currencies.filter((c) => c.isActive !== false);
    const rows = active.map((c) => ({
      value: String(c.id),
      label: c.name,
    }));
    if (existing?.currencyId && existing?.currency && !rows.some((o) => o.value === String(existing.currencyId))) {
      rows.push({
        value: String(existing.currencyId),
        label: existing.currency.name,
      });
    }
    return [{ value: '', label: 'Currency (optional)' }, ...rows];
  }, [currencies, existing?.currency, existing?.currencyId]);
  const currencyTotalCaption = React.useMemo(() => {
    const selectedId = formData.currencyId ? Number(formData.currencyId) : NaN;
    const selected = Number.isFinite(selectedId)
      ? currencies.find((c: any) => c.id === selectedId)
      : undefined;
    const symbol = (selected?.symbol ?? existing?.currency?.symbol ?? '').trim();
    return symbol ? `(${symbol}) Total` : 'Total';
  }, [currencies, formData.currencyId, existing?.currency?.symbol]);
  const lineSums = React.useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        acc.unitTotal += asFinite(line.unitTotal);
        acc.discount += asFinite(line.discount);
        acc.vat += asFinite(line.vat);
        acc.total += asFinite(line.lineTotal);
        acc.currencyTotal += asFinite(computeCurrencyTotal(line.lineTotal, formData.rate));
        return acc;
      },
      { unitTotal: 0, discount: 0, vat: 0, total: 0, currencyTotal: 0 },
    );
  }, [lines, formData.rate]);

  const level1Options = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const L = (it.level1 ?? '').trim();
      if (L) set.add(L);
    }
    return [
      { value: '', label: 'All' },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [items]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const f = line.level1Filter?.trim();
      const filtered = f ? levelItemsMap[f] ?? [] : items;
      const options = [
        { value: '', label: 'Select item' },
        ...filtered.map((it: any) => ({
          value: String(it.id),
          label: formatQuotationItemLabel(
            it.vendorCode,
            it.name,
            it.unitId,
            units,
            it.unit?.ucode,
          ),
        })),
      ];
      if (line.itemId && !options.some((o) => o.value === line.itemId)) {
        options.push({
          value: line.itemId,
          label: formatQuotationItemLabel(line.itemVcode, line.itemDescr, line.unitId, units),
        });
      }
      return options;
    },
    [items, levelItemsMap, units],
  );

  const setLine = (key: string, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const onLevel1FilterChange = (lineKey: string, value: string) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== lineKey) return line;
        const next: LineForm = { ...line, level1Filter: value };
        const f = value.trim();
        const previousLevel1 = (line.level1Filter ?? '').trim();
        if (f && line.itemId && previousLevel1 !== f) {
          next.itemId = '';
          next.itemDescr = '';
          next.itemVcode = '';
          next.unitId = '';
        }
        return next;
      }),
    );
  };

  const unitOptions = React.useMemo(
    () => [
      { value: '', label: 'Unit' },
      ...units.map((unit) => ({ value: String(unit.id), label: unit.ucode || unit.descr || `Unit #${unit.id}` })),
    ],
    [units],
  );

  React.useEffect(() => {
    if (!isEdit && paramEnquiryId) setSelectedEnquiryId(String(paramEnquiryId));
  }, [isEdit, paramEnquiryId]);

  React.useEffect(() => {
    if (!existing) return;
    setSelectedEnquiryId(String(existing.enqId));
    setFormData({
      quoDate: existing.quoDate ? existing.quoDate.slice(0, 10) : '',
      ref: existing.ref || '',
      notes: existing.notes || '',
      rate: existing.rate != null ? String(existing.rate) : '',
      paymentTerms: existing.paymentTerms || '',
      supplierRef: existing.supplierRef || '',
      currencyId: existing.currencyId != null ? String(existing.currencyId) : '',
    });
    const mapped = (existing.lineItems || []).map((line) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: line.qty != null ? String(line.qty) : '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      unitTotal: computeUnitTotal(
        line.qty != null ? String(line.qty) : '',
        line.unitPrice != null ? String(line.unitPrice) : '',
      ),
      lineTotal:
        line.lineTotal != null
          ? String(line.lineTotal)
          : computeFinalTotal(
              computeUnitTotal(
                line.qty != null ? String(line.qty) : '',
                line.unitPrice != null ? String(line.unitPrice) : '',
              ),
              line.discount != null ? String(line.discount) : '',
              line.vat != null ? String(line.vat) : '',
            ),
      discount: line.discount != null ? String(line.discount) : '',
      vat: line.vat != null ? String(line.vat) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
    const expMapped = (existing.expenses || []).map((exp) => ({
      key: `exp-${exp.id || Math.random()}`,
      description: exp.description || '',
      value: exp.value != null ? String(exp.value) : '',
      vat: exp.vat != null ? String(exp.vat) : '',
    }));
    setExpenses(expMapped.length > 0 ? expMapped : [newExpense()]);
  }, [existing]);

  const level1HydratedRef = React.useRef(false);
  React.useEffect(() => {
    level1HydratedRef.current = false;
  }, [quotationId]);
  React.useEffect(() => {
    if (!items.length || level1HydratedRef.current) return;
    setLines((prev) =>
      prev.map((line) => {
        if (!line.itemId) return line;
        const row = itemsById.get(line.itemId);
        if (!row) return line;
        return { ...line, level1Filter: (row.level1 ?? '').trim() };
      }),
    );
    level1HydratedRef.current = true;
  }, [items.length, itemsById, quotationId]);

  const copyLinesFromEnquiry = React.useCallback(() => {
    if (!selectedEnquiry || !Array.isArray(selectedEnquiry.lines)) return;
    const mapped = selectedEnquiry.lines.map((line: any) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: qtyToFormString(line.qty),
      unitPrice: '',
      unitTotal: '',
      lineTotal: '',
      discount: '',
      vat: '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [selectedEnquiry]);

  const onItemChange = (lineKey: string, itemId: string) => {
    if (!itemId) {
      setLine(lineKey, { itemId: '', itemDescr: '', itemVcode: '', unitId: '' });
      return;
    }
    const row = itemsById.get(itemId);
    setLine(lineKey, {
      itemId,
      itemVcode: row?.vendorCode || '',
      itemDescr: row?.name || '',
      unitId: row?.unitId ? String(row.unitId) : '',
      level1Filter: (row?.level1 ?? '').trim(),
    });
  };

  const normalizedLines: SupplierQuotationLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        unitId: asNumber(line.unitId),
        qty: asQtyInteger(line.qty),
        unitPrice: asNumber(line.unitPrice),
        lineTotal: asNumber(line.lineTotal),
        discount: asNumber(line.discount),
        vat: asNumber(line.vat),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter((line) => line.itemId || line.itemDescr || line.qty || line.itemNotes || line.discount != null || line.vat != null);
  }, [lines]);

  const normalizedExpenses: SupplierQuotationExpense[] = React.useMemo(() => {
    return expenses
      .map((row) => ({
        description: row.description.trim() || undefined,
        value: asNumber(row.value),
        vat: asNumber(row.vat),
      }))
      .filter((row) => row.description || row.value != null || row.vat != null);
  }, [expenses]);

  const submit = async () => {
    if (editBlockedByEvaluationOrOrder) return;
    const enqId = asNumber(selectedEnquiryId);
    if (!enqId) return;
    setIsSaving(true);
    try {
      const payload = {
        quoDate: formData.quoDate || undefined,
        enqId,
        ref: formData.ref || undefined,
        notes: formData.notes || undefined,
        rate: asNumber(formData.rate),
        paymentTerms: formData.paymentTerms || undefined,
        supplierRef: formData.supplierRef || undefined,
        currencyId: asNumber(formData.currencyId),
        lineItems: normalizedLines,
        expenses: normalizedExpenses,
      };
      if (isEdit && quotationId) await updateSupplierQuotation(quotationId, payload);
      else await createSupplierQuotation(payload);
      goBack();
    } finally {
      setIsSaving(false);
    }
  };

  
  const quotationCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = existing?.caseId ?? null;
    if (caseIdN == null || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.supplyCase ?? {
          id: caseIdN,
        },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, existing?.caseId, existing?.supplyCase]);
  const quotationEnquiryCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const ref = existing?.enquiry?.ref?.trim() || (existing?.enqId != null ? `ENQ-${existing.enqId}` : '');
    if (!ref) return null;
    const date = existing?.enquiry?.enqDate ? formatDateGlobal(existing.enquiry.enqDate) : '';
    return `Enquiry: ${ref}${date ? ` - ${date}` : ''}`;
  }, [isEdit, existing?.enquiry?.ref, existing?.enquiry?.enqDate, existing?.enqId]);
  const linkedOrderCount = React.useMemo(() => {
    if (!isEdit || !quotationId) return 0;
    const rows = Array.isArray(purchaseOrdersRes) ? purchaseOrdersRes : purchaseOrdersRes?.data ?? [];
    return rows.filter((po: any) => Number(po?.quotationId) === quotationId).length;
  }, [isEdit, quotationId, purchaseOrdersRes]);
  const linkedEvaluationCount = React.useMemo(() => {
    if (!isEdit || !quotationId) return 0;
    const rows = Array.isArray(evaluationsRes) ? evaluationsRes : evaluationsRes?.data ?? [];
    return rows.filter((ev: any) => {
      const ids = [
        ev?.quotation1Id,
        ev?.quotation2Id,
        ev?.quotation3Id,
        ev?.quotation4Id,
        ev?.quotation5Id,
      ]
        .map((v) => (v == null ? null : Number(v)))
        .filter((v): v is number => Number.isFinite(v));
      const summaryByQuote = Array.isArray(ev?.summary?.byQuote) ? ev.summary.byQuote : [];
      const summaryIds = summaryByQuote
        .map((b: any) => Number(b?.quotationId))
        .filter((v: number) => Number.isFinite(v));
      return [...ids, ...summaryIds].includes(quotationId);
    }).length;
  }, [isEdit, quotationId, evaluationsRes]);
  const editBlockedByEvaluationOrOrder =
    isEdit && (linkedEvaluationCount > 0 || linkedOrderCount > 0);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Card variant="outlined" className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5', 'border shadow-sm')}>
        <Button type="button" variant="ghost" size="sm" className={cn('-ml-2 gap-1.5 font-medium', colors.text)} onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <Button type="submit" disabled={isSaving || loadingExisting || !asNumber(selectedEnquiryId) || editBlockedByEvaluationOrOrder} loading={isSaving} size="default">
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Card>

      {quotationCaseCaption ? (
        <Callout variant="success" title="Linked supply case">
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm',
                    'border-green-200/90 bg-green-100/70 text-green-950 dark:border-green-700/60 dark:bg-green-900/40 dark:text-green-100',
                  )}
                >
                  {quotationCaseCaption}
                </span>
                {quotationEnquiryCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {quotationEnquiryCaption}
                  </span>
                ) : null}
              </div>
            </Callout>
      ) : null}
      {editBlockedByEvaluationOrOrder ? (
        <Callout variant="danger" title="Cannot edit quotation">{supplyBlockedActionMessage('QUO_EDIT_LINKED_EVAL_OR_ORDER')}</Callout>
      ) : null}

      <fieldset disabled={editBlockedByEvaluationOrOrder} className="space-y-5">
      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Quotation details"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-3">
                <Label htmlFor="quo-date">Quotation date</Label>
                <DateInput id="quo-date" value={formData.quoDate} onChange={(e) => setFormData((s) => ({ ...s, quoDate: e.target.value }))} placeholder="Select date" showFormatHint={false} className="h-10 min-h-[2.5rem]" />
              </div>
              {!isEdit ? (
                <div className="md:col-span-3">
                  <Label htmlFor="quo-enquiry">Enquiry</Label>
                  <Select id="quo-enquiry" value={selectedEnquiryId} onChange={(e) => setSelectedEnquiryId(e.target.value)} options={enquiryOptions} placeholder="Select enquiry" showClearButton={false} className="h-10 min-h-[2.5rem]" />
                </div>
              ) : null}
              <div className="md:col-span-2">
                <Label htmlFor="quo-ref">Reference</Label>
                <TextInput id="quo-ref" value={formData.ref} onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))} placeholder="Reference" className="h-10 min-h-[2.5rem]" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="quo-currency">Currency</Label>
                <Select
                  id="quo-currency"
                  value={formData.currencyId}
                  onChange={(e) => setFormData((s) => ({ ...s, currencyId: e.target.value }))}
                  options={currencyOptions}
                  showClearButton={false}
                  className="h-10 min-h-[2.5rem]"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="quo-rate">Rate</Label>
                <NumberInput id="quo-rate" value={formData.rate} onChange={(e) => setFormData((s) => ({ ...s, rate: e.target.value == null ? '' : String(e.target.value) }))} showClearButton={false} placeholder="Rate" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal" />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="quo-supplier-ref">Supplier ref</Label>
                <TextInput id="quo-supplier-ref" value={formData.supplierRef} onChange={(e) => setFormData((s) => ({ ...s, supplierRef: e.target.value }))} placeholder="Supplier reference" className="h-10 min-h-[2.5rem]" />
              </div>
              <div className="md:col-span-5">
                <Label htmlFor="quo-payment-terms">Payment terms</Label>
                <TextInput id="quo-payment-terms" value={formData.paymentTerms} onChange={(e) => setFormData((s) => ({ ...s, paymentTerms: e.target.value }))} placeholder="Payment terms" className="h-10 min-h-[2.5rem]" />
              </div>
              <div className="md:col-span-4">
                <Label htmlFor="quo-notes">Notes</Label>
                <Textarea id="quo-notes" value={formData.notes} onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))} rows={2} showClearButton={false} className="min-h-[72px]" />
              </div>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Expenses"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Add expense"
                aria-label="Add expense"
                onClick={() => setExpenses((prev) => [...prev, newExpense()])}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="min-w-[10rem] px-2 py-2 text-left text-xs font-medium">Description</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">Value</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">VAT</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((row) => (
                    <tr key={row.key} className="align-top">
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={row.description}
                          onChange={(e) =>
                            setExpenses((prev) => prev.map((r) => (r.key === row.key ? { ...r, description: e.target.value } : r)))
                          }
                          showClearButton={false}
                          placeholder="Description"
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.value}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, value: e.target.value == null ? '' : String(e.target.value) } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="Value"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.vat}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, vat: e.target.value == null ? '' : String(e.target.value) } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() => setExpenses((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== row.key) : [newExpense()]))}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item Discount (%)</span>
                <NumberInput
                  size="sm"
                  value={bulkDiscountPct}
                  onChange={(e) => setBulkDiscountPct(e.target.value == null ? '' : String(e.target.value))}
                  placeholder="%"
                  showClearButton={false}
                  className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const pct = Number(bulkDiscountPct);
                    if (!Number.isFinite(pct)) return;
                    setLines((prev) =>
                      prev.map((r) => {
                        const unit = Number(r.unitTotal);
                          const discountValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                        return {
                          ...r,
                          discount: discountValue,
                          lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                        };
                      }),
                    );
                  }}
                  disabled={!lines.length}
                >
                  Apply
                </Button>
                </div>
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item VAT (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkVatPct}
                    onChange={(e) => setBulkVatPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkVatPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const vatValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            vat: vatValue,
                            lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
              </div>
              <Button type="button" variant="outline" size="icon" title="Add line" aria-label="Add line" onClick={() => setLines((prev) => [...prev, newLine()])}>
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right text-xs font-medium">Unit</th>
                    <th className="w-24 px-2 py-2 text-left text-xs font-medium">Qty</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Price</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Unit Total</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Discount</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">VAT</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Total</th>
                    <th className="w-28 px-2 py-2 text-right text-xs font-medium">{currencyTotalCaption}</th>
                    <th className="w-[12rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.key} className="align-top">
                      <td className="p-2">
                        <Select
                          name={`quo-line-l1-${line.key}`}
                          value={line.level1Filter}
                          onChange={(e) => onLevel1FilterChange(line.key, e.target.value)}
                          options={level1Options}
                          placeholder="Level 1"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          name={`quo-line-item-${line.key}`}
                          value={line.itemId}
                          onChange={(e) => onItemChange(line.key, e.target.value)}
                          options={getItemOptionsForLine(line)}
                          placeholder="Select item"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full min-w-[12rem] text-sm"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Select value={line.unitId} onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, unitId: e.target.value } : r)))} options={unitOptions} placeholder="Unit" showClearButton={false} className="h-10 min-h-[2.5rem]" />
                      </td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={line.qty} allowDecimals={false} step={1} onChange={(e) => setLines((prev) => prev.map((r) => {
                        if (r.key !== line.key) return r;
                        const qtyValue = e.target.value == null ? '' : String(e.target.value);
                        const unitTotal = computeUnitTotal(qtyValue, r.unitPrice);
                        return {
                          ...r,
                          qty: qtyValue,
                          unitTotal,
                          lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                        };
                      }))} showClearButton={false} placeholder="Qty" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal" /></td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={line.unitPrice} onChange={(e) => setLines((prev) => prev.map((r) => {
                        if (r.key !== line.key) return r;
                        const priceValue = e.target.value == null ? '' : String(e.target.value);
                        const unitTotal = computeUnitTotal(r.qty, priceValue);
                        return {
                          ...r,
                          unitPrice: priceValue,
                          unitTotal,
                          lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                        };
                      }))} showClearButton={false} placeholder="Price" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal" /></td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={line.unitTotal} disabled showClearButton={false} placeholder="Unit Total" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right" /></td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={line.discount} onChange={(e) => setLines((prev) => prev.map((r) => {
                        if (r.key !== line.key) return r;
                        const discountValue = e.target.value == null ? '' : String(e.target.value);
                        return {
                          ...r,
                          discount: discountValue,
                          lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                        };
                      }))} showClearButton={false} placeholder="Discount" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right" /></td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={line.vat} onChange={(e) => setLines((prev) => prev.map((r) => {
                        if (r.key !== line.key) return r;
                        const vatValue = e.target.value == null ? '' : String(e.target.value);
                        return {
                          ...r,
                          vat: vatValue,
                          lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                        };
                      }))} showClearButton={false} placeholder="VAT" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right" /></td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={line.lineTotal} disabled showClearButton={false} placeholder="Total" className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right" /></td>
                      <td className="p-2 text-right"><NumberInput size="sm" value={computeCurrencyTotal(line.lineTotal, formData.rate)} disabled showClearButton={false} placeholder={currencyTotalCaption} className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right" /></td>
                      <td className="p-2"><TextInput size="sm" value={line.itemNotes} onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, itemNotes: e.target.value } : r)))} showClearButton={false} placeholder="Notes" className="h-10 min-h-[2.5rem]" /></td>
                      <td className="p-2 text-right">
                        <Button type="button" variant="danger" size="icon" onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== line.key) : [newLine()]))}>
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={cn('border-t', colors.border)}>
                    <td colSpan={5} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                      SUM
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Unit Total SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.unitTotal)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Discount SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.discount)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.vat)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>TOTAL SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.total)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>{currencyTotalCaption} SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.currencyTotal)}</div>
                    </td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>
      </fieldset>
    </form>
  );
};

export default Form;

